import React from 'react';
import './ReviewSubmit.css';

const ReviewSubmit = ({
    formData,
    completedSteps = [],
    steps = [],
    onEditStep,
    onSaveDraft,
    onSubmit,
    isSaving = false,
    isSubmitting = false,
    submitError = '',
    /*
     * readOnly: for support's "view accommodation" / pending-
     * approval review page, which reuses this same summary
     * instead of duplicating it - there's nothing to edit, save
     * as a draft, or submit there, so the per-section Edit
     * buttons, the "ready to submit?" completion checklist, and
     * the Save/Submit footer are all hidden. The caller renders
     * its own Approve/Reject actions instead (see `actions`).
     */
    readOnly = false,
    actions = null,
    /*
     * activeSection: when set, only the matching <section> below is
     * rendered instead of all nine stacked one after another. Added
     * so the general "view accommodation" page (support's My
     * Packages -> card -> details) can present these same sections
     * as tabs instead of one long scroll, without touching how this
     * component renders anywhere else - the wizard's own Review
     * step and the pending-approval page (a different page, with
     * its own Approve/Reject actions) never pass this prop, so they
     * keep rendering every section exactly as before.
     */
    activeSection = null
}) => {
    const units = formData?.units || [];
    const amenities = formData?.amenities || {};
    const services = formData?.services || {};
    const policies = formData?.policies || {};
    const media = formData?.media || {};
    const additional = formData?.additional || {};

    const getStepIndex = (id) => steps.findIndex((step) => step.id === id);

    const isCompleted = (key) => {
        const index = getStepIndex(key);
        return index !== -1 && completedSteps.includes(index);
    };

    const handleEdit = (key) => {
        const index = getStepIndex(key);

        if (index !== -1 && onEditStep) {
            onEditStep(index);
        }
    };

    const getPropertyTypeLabel = () => {
        if (!formData.propertyType) return 'Not specified';

        return String(formData.propertyType)
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getUnitTypeLabel = (type) => {
        if (!type) return 'Unit';

        return String(type)
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    /*
     * unit.pricing is the single pricing object each unit editor
     * writes (name, actual, tax, total, currency, ...) - matches
     * UnitCard's own getPrice() logic. There is no unit.pricing.
     * basePrice on the frontend shape (that name only exists in
     * the BACKEND payload buildAccommodationPayload() produces).
     */
    const getPrice = (unit) => {
        const price = unit?.pricing?.total || unit?.pricing?.actual;

        if (!price) {
            return null;
        }

        return price;
    };

    const getCurrency = (unit) => unit?.pricing?.currency || 'INR';

    const formatCurrency = (value, currency = 'INR') => {
        if (value === undefined || value === null || value === '') {
            return 'Not specified';
        }

        try {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency,
                maximumFractionDigits: 0
            }).format(Number(value));
        } catch {
            return `${currency} ${value}`;
        }
    };

    const getImageUrl = (image) => {
        if (!image) return '';

        if (typeof image === 'string') {
            return image;
        }

        return image.preview || image.url || image.src || '';
    };

    const propertyImages = Array.isArray(media.images)
        ? media.images
        : Array.isArray(formData.images)
          ? formData.images
          : [];

    const getAmenityCount = () => {
        if (Array.isArray(amenities)) {
            return amenities.length;
        }

        let count = 0;

        Object.values(amenities).forEach((value) => {
            if (Array.isArray(value)) {
                count += value.length;
            } else if (value && typeof value === 'object') {
                count += Object.values(value).filter(Boolean).length;
            } else if (value) {
                count += 1;
            }
        });

        return count;
    };

    const getServiceCount = () => {
        if (Array.isArray(services)) {
            return services.length;
        }

        let count = 0;

        Object.values(services).forEach((value) => {
            if (Array.isArray(value)) {
                count += value.length;
            } else if (value && typeof value === 'object') {
                count += Object.keys(value).length;
            }
        });

        return count;
    };

    /*
     * nearbyPlaces and faqs are root-level formData fields (same
     * ones AdditionalInformation.jsx reads/writes), not nested
     * under formData.additional - that object only holds the
     * check-in/guest-instruction scratch fields.
     */
    const nearbyPlaces = Array.isArray(formData?.nearbyPlaces) ? formData.nearbyPlaces : [];

    const faqs = Array.isArray(formData?.faqs) ? formData.faqs : [];

    return (
        <div className='review-submit'>
            {/* =====================================================
                HEADER
            ====================================================== */}

            <div className='review-header'>
                <div>
                    <span className='review-eyebrow'>Final Review</span>

                    <h2>Review your accommodation</h2>

                    <p>Check all the information before submitting your accommodation for review.</p>
                </div>
            </div>

            {/* =====================================================
                SUBMISSION ERROR
            ====================================================== */}

            {submitError && (
                <div className='review-submit-error'>
                    <span>!</span>

                    <div>
                        <strong>Unable to submit</strong>
                        <p>{submitError}</p>
                    </div>
                </div>
            )}

            {/* =====================================================
                BASIC INFORMATION
            ====================================================== */}

            {(!activeSection || activeSection === 'basic') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>01</span>

                        <div>
                            <h3>Basic Information</h3>

                            <p>Property name and general information</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('basic')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-property-summary'>
                    <div className='review-property-main'>
                        <h4>{formData.title || 'Untitled accommodation'}</h4>

                        <span>{getPropertyTypeLabel()}</span>
                    </div>

                    {formData.overview && <p className='review-description'>{formData.overview}</p>}

                    <div className='review-info-grid'>
                        <div>
                            <span>Maximum guests</span>
                            <strong>{formData.maxGuests || '—'}</strong>
                        </div>

                        <div>
                            <span>Bedrooms</span>
                            <strong>{formData.totalBedrooms || '—'}</strong>
                        </div>

                        <div>
                            <span>Bathrooms</span>
                            <strong>{formData.totalBathrooms || '—'}</strong>
                        </div>

                        <div>
                            <span>Units</span>
                            <strong>{units.length}</strong>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* =====================================================
                LOCATION
            ====================================================== */}

            {(!activeSection || activeSection === 'location') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>02</span>

                        <div>
                            <h3>Location</h3>

                            <p>Property address and location details</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('location')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-location-box'>
                    <strong>{formData.address?.line1 || 'Address not specified'}</strong>

                    <p>
                        {[formData.address?.city, formData.address?.state, formData.address?.country, formData.address?.pincode]
                            .filter(Boolean)
                            .join(', ') || 'Location details not specified'}
                    </p>
                </div>
            </section>
            )}

            {/* =====================================================
                UNITS
            ====================================================== */}

            {(!activeSection || activeSection === 'units') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>03</span>

                        <div>
                            <h3>Accommodation Units</h3>

                            <p>Rooms, tents, villas or other bookable units</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('units')}>
                            Edit
                        </button>
                    )}
                </div>

                {units.length === 0 ? (
                    <div className='review-empty'>
                        <span>🏕️</span>
                        <p>No accommodation units added.</p>
                    </div>
                ) : (
                    <div className='review-unit-list'>
                        {units.map((unit, index) => {
                            const image = getImageUrl(unit.images?.[0]);

                            const price = getPrice(unit);

                            return (
                                <div className='review-unit-item' key={unit.id || index}>
                                    <div className='review-unit-image'>
                                        {image ? (
                                            <img src={image} alt={unit.name || 'Accommodation unit'} />
                                        ) : (
                                            <span>🏕️</span>
                                        )}
                                    </div>

                                    <div className='review-unit-content'>
                                        <div className='review-unit-title-row'>
                                            <h4>{unit.name || `Unit ${index + 1}`}</h4>

                                            <span>{getUnitTypeLabel(unit.unitType)}</span>
                                        </div>

                                        <div className='review-unit-meta'>
                                            {unit.occupancy?.maxGuests && (
                                                <span>👤 {unit.occupancy.maxGuests} guests</span>
                                            )}

                                            {unit.bedrooms && <span>🛏️ {unit.bedrooms} bedrooms</span>}

                                            {unit.bathrooms && <span>🚿 {unit.bathrooms} bathrooms</span>}

                                            {unit.inventory?.totalUnits && (
                                                <span>📦 {unit.inventory.totalUnits} available</span>
                                            )}
                                        </div>
                                    </div>

                                    {price !== null && (
                                        <div className='review-unit-price'>
                                            <strong>{formatCurrency(price, getCurrency(unit))}</strong>

                                            <span>{unit.pricing?.unit || 'per night'}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
            )}

            {/* =====================================================
                AMENITIES
            ====================================================== */}

            {(!activeSection || activeSection === 'amenities') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>04</span>

                        <div>
                            <h3>Amenities</h3>

                            <p>Facilities and features available to guests</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('amenities')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-stat-box'>
                    <strong>{getAmenityCount()}</strong>
                    <span>Amenities configured</span>
                </div>
            </section>
            )}

            {/* =====================================================
                PRICING
            ====================================================== */}

            {(!activeSection || activeSection === 'pricing') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>05</span>

                        <div>
                            <h3>Pricing</h3>

                            <p>Property pricing and booking configuration</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('pricing')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-info-grid'>
                    <div>
                        <span>Currency</span>
                        <strong>{getCurrency(units[0])}</strong>
                    </div>

                    <div>
                        <span>Units priced</span>
                        <strong>
                            {units.filter((unit) => getPrice(unit) !== null).length} / {units.length}
                        </strong>
                    </div>

                    <div>
                        <span>Minimum stay</span>
                        <strong>
                            {/* minStay = what the Policies step edits */}
                            {formData.policies?.minStay ?? formData.policies?.minimumStay
                                ? `${formData.policies.minStay ?? formData.policies.minimumStay} night(s)`
                                : '—'}
                        </strong>
                    </div>

                    <div>
                        <span>Seasonal pricing rules</span>
                        <strong>{Array.isArray(formData.seasonalPricing) ? formData.seasonalPricing.length : 0}</strong>
                    </div>
                </div>
            </section>
            )}

            {/* =====================================================
                SERVICES
            ====================================================== */}

            {(!activeSection || activeSection === 'services') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>06</span>

                        <div>
                            <h3>Services</h3>

                            <p>Additional services offered to guests</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('services')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-stat-box'>
                    <strong>{getServiceCount()}</strong>
                    <span>Services configured</span>
                </div>
            </section>
            )}

            {/* =====================================================
                POLICIES
            ====================================================== */}

            {(!activeSection || activeSection === 'policies') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>07</span>

                        <div>
                            <h3>Policies</h3>

                            <p>Cancellation, house rules and booking policies</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('policies')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-policy-list'>
                    <div>
                        <span>Cancellation</span>

                        <strong>
                            {policies.cancellation?.type
                                ? String(policies.cancellation.type)
                                      .replace(/_/g, ' ')
                                      .replace(/\b\w/g, (char) => char.toUpperCase())
                                : 'Not specified'}
                        </strong>
                    </div>

                    <div>
                        <span>Smoking</span>

                        <strong>
                            {(policies.houseRules || []).includes('No smoking') ? 'Not allowed' : 'Not specified'}
                        </strong>
                    </div>

                    <div>
                        <span>Pets</span>

                        <strong>
                            {(policies.houseRules || []).includes('Pets allowed') ? 'Allowed' : 'Not specified'}
                        </strong>
                    </div>
                </div>
            </section>
            )}

            {/* =====================================================
                MEDIA
            ====================================================== */}

            {(!activeSection || activeSection === 'media') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>08</span>

                        <div>
                            <h3>Media</h3>

                            <p>Photos and videos of the accommodation</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('media')}>
                            Edit
                        </button>
                    )}
                </div>

                {propertyImages.length === 0 ? (
                    <div className='review-empty'>
                        <span>📷</span>
                        <p>No property images added.</p>
                    </div>
                ) : (
                    <div className='review-media-grid'>
                        {propertyImages.slice(0, 6).map((image, index) => {
                            const imageUrl = getImageUrl(image);

                            return (
                                <div className='review-media-item' key={index}>
                                    {imageUrl ? <img src={imageUrl} alt={`Property ${index + 1}`} /> : <div>📷</div>}

                                    {index === 0 && <span className='review-cover-badge'>Cover</span>}
                                </div>
                            );
                        })}

                        {propertyImages.length > 6 && (
                            <div className='review-more-media'>+{propertyImages.length - 6}</div>
                        )}
                    </div>
                )}
            </section>
            )}

            {/* =====================================================
                ADDITIONAL INFORMATION
            ====================================================== */}

            {(!activeSection || activeSection === 'additional') && (
            <section className='review-card'>
                <div className='review-card-header'>
                    <div>
                        <span className='review-card-number'>09</span>

                        <div>
                            <h3>Additional Information</h3>

                            <p>Guest instructions and property information</p>
                        </div>
                    </div>

                    {!readOnly && (
                        <button type='button' className='review-edit-button' onClick={() => handleEdit('additional')}>
                            Edit
                        </button>
                    )}
                </div>

                <div className='review-additional-grid'>
                    <div>
                        <span>Check-in</span>

                        <strong>
                            {additional.checkInFrom || additional.checkInUntil
                                ? `${additional.checkInFrom || '—'} - ${additional.checkInUntil || '—'}`
                                : 'Not specified'}
                        </strong>
                    </div>

                    <div>
                        <span>Check-out</span>

                        <strong>
                            {additional.checkOutFrom || additional.checkOutUntil
                                ? `${additional.checkOutFrom || '—'} - ${additional.checkOutUntil || '—'}`
                                : 'Not specified'}
                        </strong>
                    </div>

                    <div>
                        <span>Nearby places</span>

                        <strong>{nearbyPlaces.length}</strong>
                    </div>

                    <div>
                        <span>FAQs</span>

                        <strong>{faqs.length}</strong>
                    </div>
                </div>
            </section>
            )}

            {!readOnly && (
                <>
                    {/* =====================================================
                        COMPLETION STATUS
                    ====================================================== */}

                    <section className='review-completion-card'>
                        <div className='review-completion-icon'>✓</div>

                        <div className='review-completion-content'>
                            <h3>Ready to submit?</h3>

                            <p>
                                Make sure all required information is complete. You can still edit any section before
                                submitting.
                            </p>

                            <div className='review-completion-status'>
                                {steps
                                    .filter((step) => step.id !== 'review')
                                    .map((step) => {
                                        const index = getStepIndex(step.id);

                                        const completed = index !== -1 && completedSteps.includes(step.id);

                                        return (
                                            <span key={step.id} className={completed ? 'completed' : 'pending'}>
                                                {completed ? '✓' : '○'} {step.label}
                                            </span>
                                        );
                                    })}
                            </div>
                        </div>
                    </section>

                    {/* =====================================================
                        ACTIONS
                    ====================================================== */}

                    <div className='review-submit-actions'>
                        <button
                            type='button'
                            className='review-save-button'
                            onClick={onSaveDraft}
                            disabled={isSaving || isSubmitting}
                        >
                            {isSaving ? 'Saving...' : 'Save as Draft'}
                        </button>

                        <button
                            type='button'
                            className='review-submit-button'
                            onClick={onSubmit}
                            disabled={isSaving || isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Accommodation'}
                        </button>
                    </div>

                    <p className='review-submit-note'>
                        By submitting this accommodation, you confirm that the information provided is accurate and
                        ready for review.
                    </p>
                </>
            )}

            {/*
             * Read-only mode (support's pending-approval review):
             * the caller passes its own Approve/Reject (or just a
             * Back) actions here instead of the create/edit footer
             * above.
             */}
            {readOnly && actions && <div className='review-submit-actions'>{actions}</div>}
        </div>
    );
};

export default ReviewSubmit;
